import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { email, form, FormField, pattern, required } from '@angular/forms/signals';
import { Router, RouterModule } from '@angular/router';
import { AuthApiService } from '@core/services/api/auth.service';
import { environment } from '@env/environment.prod';
import { AuthStateService } from '@shared/services/auth-state.service';
import { DeviceService } from '@shared/services/device.service';
import { LocalStorageService } from '@shared/services/local-storage.service';

@Component({
  selector: 'app-auth-login',
  templateUrl: './login.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterModule, FormField],
})
export class AuthLogin implements OnInit {
  private readonly authService = inject(AuthApiService);
  private readonly authState = inject(AuthStateService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly deviceService = inject(DeviceService);

  ngOnInit(): void {
    // Si el usuario ya tiene una sesión guardada, la restauramos
    if (this.deviceService.isBrowser()) {
      const recordarme = this.localStorageService.getRucAndEmail();
      if (recordarme) {
        const { ruc, email } = recordarme;
        this.loginForm().controlValue.set({ ruc, email, password: '', rememberMe: true });
      }
    }
  }

  private readonly router = inject(Router);
  errorLogin = signal('');
  loginForm = form(
    signal({
      ruc: '',
      email: '',
      password: '',
      rememberMe: false,
    }),
    (schemaPath) => {
      required(schemaPath.ruc, { message: 'RUC is required' });
      required(schemaPath.email, { message: 'Email is required' });
      email(schemaPath.email, { message: 'Enter a valid email address' });
      required(schemaPath.password, { message: 'Password is required' });
      pattern(schemaPath.password, environment.strongPasswordRegex, {
        message:
          'La contraseña debe tener al menos 8 caracteres, incluyendo una letra mayúscula, una letra minúscula y un número.',
      });
    },
  );
  showPassword = signal(false);
  togglePasswordVisibility() {
    this.showPassword.set(!this.showPassword());
  }

  onLogin(event: Event) {
    event.preventDefault();

    if (this.loginForm().invalid()) {
      this.errorLogin.set('Por favor, completa todos los campos correctamente.');
      return;
    }
    this.errorLogin.set('');
    const { ruc, email, password, rememberMe } = this.loginForm().value();
    if (rememberMe) {
      this.localStorageService.setRucAndEmail(ruc.trim(), email.trim());
    } else {
      this.localStorageService.removeRucAndEmail();
    }

    this.authService.login(email.trim(), password.trim(), ruc.trim()).subscribe({
      next: (rs) => {
        this.authState.setSession(rs);
        this.router.navigate(['/panel']);
      },
      error: (err) => {
        console.error('Login error:', err);
        const serverMsg = err?.error?.message;
        if (err.status === 401) {
          this.errorLogin.set(
            serverMsg || 'Credenciales incorrectas. Por favor, verifica tu email, contraseña y RUC.',
          );
        } else if (err.status === 403) {
          this.errorLogin.set(
            serverMsg || 'Tu cuenta no tiene permisos para acceder. Contacta al administrador.',
          );
        } else if (err.status === 404) {
          this.errorLogin.set(serverMsg || 'Servicio no disponible. Verifica la URL del servidor.');
        } else if (err.status === 429) {
          this.errorLogin.set(serverMsg || 'Demasiados intentos. Espera un momento e inténtalo de nuevo.');
        } else if (err.status >= 500) {
          this.errorLogin.set(serverMsg || 'Error del servidor. Por favor, intenta nuevamente más tarde.');
        } else if (err.status === 0) {
          this.errorLogin.set(
            serverMsg || 'No se pudo conectar al servidor. Verifica tu conexión a internet.',
          );
        } else {
          this.errorLogin.set(serverMsg || 'Error inesperado al iniciar sesión. Intenta nuevamente.');
        }
      },
    });
  }
  onChangeRecuerdame(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    console.log('Recordarme changed:', checked);
    if (!checked) {
      this.localStorageService.removeRucAndEmail();
    } else {
      const { ruc, email } = this.loginForm().value();
      this.localStorageService.setRucAndEmail(ruc.trim(), email.trim());
    }
  }
}
